import React from "react";
import { SvgIconTypeMap } from "@mui/material";
import { AutoAwesome, StackedBarChart, TextFields, Map, TableRows, DesignServices, SettingsSuggest } from '@mui/icons-material';
import { OverridableComponent } from "@mui/material/OverridableComponent";

import DashboardList from "components/widgets/lists/DashboardList/DashboardList";
import DefaultWidget from "components/widgets/DefaultWidget";
import TemplateSearch from "components/widgets/utility/TemplateSearch/TemplateSearch";

// Widget Imports
import AISummarySettings, { BuildDefaultAISummaryRequests } from "components/widgets/ai/AISummary/AISummarySettings";
import AISocialPersonasStructured from "components/widgets/ai/AISocialPersonas/AISocialPersonasStructured";
import AISummaryStructured from "components/widgets/ai/AISummary/AISummaryStructured"
import ArcChart from "components/widgets/charts/ArcChart/ArcChart";
import ArcChartSettings, { BuildDefaultSinglePercentageValueRequestsFunction, defaultArcChartRequest } from "components/widgets/charts/ArcChart/ArcChartSettings";
import BarChart from "components/widgets/charts/BarChart/BarChart";
import BarChartSettings, { BuildDefaultBarChartRequests } from "components/widgets/charts/BarChart/BarChartSettings";
import CategoryArcs from "components/widgets/charts/CategoryArcs/CategoryArcs";
import CategoryArcsSettings, { BuildDefaultCategoryArcsRequests } from "components/widgets/charts/CategoryArcs/CategoryArcsSettings";
import CategoryBars from "components/widgets/charts/CategoryBars/CategoryBars";
import CategoryBarsSettings, { BuildDefaultCategoryBarsRequests } from "components/widgets/charts/CategoryBars/CategoryBarsSettings";
import ChordChart from "components/widgets/charts/ChordChart/ChordChart";
import ChordChartSettings, { BuildDefaultChordChartRequests } from "components/widgets/charts/ChordChart/ChordChartSettings";
import ChoroplethMapWidget, { BuildDefaultChoroplethRequests } from "components/widgets/mapbox/Choropleth";
import ColorBox from "components/widgets/design/ColorBox/ColorBox";
import DataTable from "components/widgets/tables/DataTable/DataTable";
import DataTableSettings, { BuildDefaultDataTableRequests } from "components/widgets/tables/DataTable/DataTableSettings";
import EmbedList from "components/widgets/lists/embeds/EmbedList";
import EmbedSettings, { BuildDefaultEmbedListRequests } from "components/widgets/lists/embeds/EmbedSettings";
import ForceGraphWidget from "components/widgets/charts/ForceGraph/ForceGraphWidget";
import ForceGraphSettings, { BuildDefaultForceGraphRequests } from "components/widgets/charts/ForceGraph/ForceGraphSettings";
import Headline from "components/widgets/typography/Headline/Headline";
import HeatmapMapWidget from "components/widgets/mapbox/Heatmap";
import Image from "components/widgets/design/Image/Image";
import ImageSettings from "components/widgets/design/Image/ImageSettings";
import MentionsOverTime from "components/widgets/charts/MentionsOverTime/MentionsOverTime";
import MentionsOverTimeSettings, { BuildDefaultMentionsOverTimeRequests } from "components/widgets/charts/MentionsOverTime/MentionsOverTimeSettings";
import NarrativeTableWidget from "components/widgets/charts/NarrativeTable/NarrativeTable";
import NarrativeTableSettings, { BuildDefaultNarrativeTableRequests } from "components/widgets/charts/NarrativeTable/NarrativeTableSettings";
import NumericCard from "components/widgets/charts/NumericCard/NumericCard";
import NumericCardSettings, { BuildDefaultNumericCardRequests } from "components/widgets/charts/NumericCard/NumericCardSettings";
import PercentChange from "components/widgets/charts/PercentChange/PercentChange";
import PercentChangeSettings, { BuildDefaultPercentChangeRequests } from "components/widgets/charts/PercentChange/PercentChangeSettings";``
import PieChart from "components/widgets/charts/PieChart/PieChart";
import PieChartSettings, { BuildDefaultPieChartRequests } from "components/widgets/charts/PieChart/PieChartSettings";
import PostList from "components/widgets/lists/posts/PostList";
import PostListSettings, { BuildDefaultPostListRequests } from "components/widgets/lists/posts/PostListSettings";
import RadarChart from "components/widgets/charts/RadarChart/RadarChart";
import RadarChartSettings, { BuildDefaultRadarChartRequests } from "components/widgets/charts/RadarChart/RadarChartSettings";
import RadialSentiment from "components/widgets/charts/RadialSentiment/RadialSentiment";
import RadialSentimentSettings, { BuildDefaultRadialSentimentRequests } from "components/widgets/charts/RadialSentiment/RadialSentimentSettings";
import RecordTable from "components/widgets/tables/RecordTable/RecordTable";
import RecordTableSettings, { BuildDefaultRecordTableRequests } from "components/widgets/tables/RecordTable/RecordTableSettings";
import RichTextEditor from "components/widgets/typography/RichTextEditor/RichTextEditor";
import SingleBarChart from "components/widgets/charts/SingleBar/SingleBar";
import SingleBarChartSettings, { defaultSingleBarChartRequest } from "../widgets/charts/SingleBar/SingleBarSettings";
import SocialInfluencersList from "components/widgets/charts/SocialInfluencersList/SocialInfluencersList";
import SocialInfluencersSettings, { BuildDefaultSocialInfluencersListRequests } from "components/widgets/charts/SocialInfluencersList/SocialInfluencersSettings";
import Sparkline from "components/widgets/charts/Sparkline/Sparkline";
import SparklineSettings, { BuildDefaultSparklineRequests } from "components/widgets/charts/Sparkline/SparklineSettings";
import TopicTableWidget from "components/widgets/charts/TopicTable/TopicTable";
import TopicTableSettings, { BuildDefaultTopicTableRequests } from "components/widgets/charts/TopicTable/TopicTableSettings";
import TopicsTreeMap from "components/widgets/charts/TopicsTreeMap/TopicsTreeMap";
import TopicsTreeMapSettings, { BuildDefaultTopicTreeRequests } from "components/widgets/charts/TopicsTreeMap/TopicsTreeMapSettings";
import TrendChart from "components/widgets/charts/TrendChart/TrendChart";
import TrendChartSettings, { BuildDefaultTrendChartRequests } from "components/widgets/charts/TrendChart/TrendChartSettings";
import WordCloud from "components/widgets/charts/WordCloud/WordCloud";
import WordCloudSettings, { BuildDefaultWordCloudRequests } from "components/widgets/charts/WordCloud/WordCloudSettings";
import NetValue from "components/widgets/charts/NetValue/NetValue";
import NetValueSettings, { BuildDefaultNetValueRequests } from "components/widgets/charts/NetValue/NetValueSettings";
import NetValueTrend from "components/widgets/charts/NetValueTrend/NetValueTrend";
import NetValueTrendSettings, { BuildDefaultNetValueTrendRequests } from "components/widgets/charts/NetValueTrend/NetValueTrendSettings";
import HeatmapSettings, { BuildDefaultHeatmapRequests } from "components/widgets/mapbox/HeatmapSettings";
import { Tag } from "routes/tagManagement/TagManagementPage";

import { QueryInfo } from "system/query/QueryInfo";
import { WidgetRequest } from "system/widget/Widget";
import ShareOfVoice from "components/widgets/charts/ShareOfVoice/ShareOfVoice";
import ShareOfVoiceSettings, { BuildDefaultShareOfVoiceRequests } from "components/widgets/charts/ShareOfVoice/ShareOfVoiceSettings";
import BubbleComparison from "components/widgets/charts/BubbleComparison/BubbleComparison";
import BubbleComparisonSettings, { BuildDefaultBubbleComparisonRequests } from "components/widgets/charts/BubbleComparison/BubbleComparisonSettings";
import TimeOfDayOfWeek from "components/widgets/charts/TimeOfDayOfWeek/TimeOfDayOfWeek";
import TimeOfDayOfWeekSettings, { BuildDefaultTimeOfDayOfWeekRequests } from "components/widgets/charts/TimeOfDayOfWeek/TimeOfDayOfWeekSettings";
import Subqueries from "components/widgets/charts/Subqueries/Subqueries";
import SubqueriesSettings, { BuildDefaultSubqueriesRequests } from "components/widgets/charts/Subqueries/SubqueriesSettings";
import QueryComparisonTable from "components/widgets/tables/QueryComparisonTable/QueryComparisonTable";
import QueryComparisonTableSettings, { buildDefaultQueriesRequest } from "components/widgets/tables/QueryComparisonTable/QueryComparisonTableSettings";
import LineChartWidget, { BuildDefaultLineChartRequests } from "components/widgets/charts/LineChart/LineChartWidget";
import LineChartSettings from "components/widgets/charts/LineChart/settings/LineChartSettings";

export const widgetCategoryIcons:{[key: string]: OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string; }} = {
  ai: AutoAwesome,
  charts: StackedBarChart,
  linguistics: TextFields,
  geography: Map,
  tables: TableRows,
  design: DesignServices,
  admin: SettingsSuggest
};

const AdminTag: Tag = { id: "admin", name: "admin" };
const AiTag: Tag = { id: "ai", name: "ai" };
const ChartsTag: Tag = { id: "charts", name: "charts" };
const DesignTag: Tag = { id: "design", name: "design" };
const GeographyTag: Tag = { id: "geography", name: "geography" };
const LinguisticsTag: Tag = { id: "linguistics", name: "linguistics" };
const NumberTag: Tag = { id: "number", name: "number" };
const QueryTag: Tag = { id: "query", name: "query" };
const TablesTag: Tag = { id: "tables", name: "tables" };
const TextTag: Tag = { id: "text", name: "text" };

export interface WidgetRegistryItem {
  id: string,
  component: any,
  category?: string,
  contentPath?: string,

  defaultEndpoint?: string,
  settingsComponent?: any,

  buildDefaultRequestBody?: (queryInfo: QueryInfo) => WidgetRequest | any,
  image?: string,
  defaultData?: any,
  requiredPermissions?: Array<string> // The widget will be disabled / wont be addable in the Add Widget list if the user doesn't have all of these permissions
  tags?: Array<Tag>;

  // Hide this widget from the add widget dialog, but still allow it to be used in templates
  hideFromAddWidgetDialog?: boolean
}

export const widgetRegistry: Array<WidgetRegistryItem> = [
  {
    id: "numeric-card",
    category: "charts",
    component: NumericCard,
    settingsComponent: NumericCardSettings,
    buildDefaultRequestBody: BuildDefaultNumericCardRequests,
    image: "/img/widgets/numeric-card.png",
    tags: [NumberTag, QueryTag],
  }, {
    id: "net-value",
    category: "charts",
    component: NetValue,
    settingsComponent: NetValueSettings,
    buildDefaultRequestBody: BuildDefaultNetValueRequests,
    image: "/img/widgets/net-value.png",
    tags: [NumberTag, QueryTag],
  }, {
    id: "net-value-trend",
    category: "charts",
    component: NetValueTrend,
    settingsComponent: NetValueTrendSettings,
    buildDefaultRequestBody: BuildDefaultNetValueTrendRequests,
    image: "/img/widgets/net-value-trend.png",
    tags: [ChartsTag, QueryTag],
  }, {
    id: "sparkline",
    category: "charts",
    component: Sparkline,
    settingsComponent: SparklineSettings,
    buildDefaultRequestBody: BuildDefaultSparklineRequests,
    image: "/img/widgets/sparkline.png",
    tags: [ChartsTag, QueryTag]
  }, {
    id: "trend-chart",
    category: "charts",
    component: TrendChart,
    settingsComponent: TrendChartSettings,
    buildDefaultRequestBody: BuildDefaultTrendChartRequests,
    image: "/img/widgets/trend-chart.png",
    tags: [ChartsTag, QueryTag],
  }, {
    id: "line-chart",
    category: "charts",
    component: LineChartWidget,
    settingsComponent: LineChartSettings,
    buildDefaultRequestBody: BuildDefaultLineChartRequests,
    image: "/img/widgets/line-chart.png",
    tags: [ChartsTag, QueryTag],
  }, {
    id: "bar-chart",
    category: "charts",
    component: BarChart,
    settingsComponent: BarChartSettings,
    buildDefaultRequestBody: BuildDefaultBarChartRequests,
    image: "/img/widgets/bar-chart.png",
    tags: [ChartsTag, QueryTag],
  }, {
    id: "single-bar-chart",
    category: "charts",
    component: SingleBarChart,
    settingsComponent: SingleBarChartSettings,
    buildDefaultRequestBody: BuildDefaultSinglePercentageValueRequestsFunction(defaultSingleBarChartRequest),
    image: "/img/widgets/single-bar-chart.png",
    tags: [ChartsTag, QueryTag],
  }, {
    id: "arc-chart",
    category: "charts",
    component: ArcChart,
    settingsComponent: ArcChartSettings,
    buildDefaultRequestBody: BuildDefaultSinglePercentageValueRequestsFunction(defaultArcChartRequest),
    image: "/img/widgets/arc-chart.png",
    tags: [ChartsTag, QueryTag],
  }, {
    id: "category-bars",
    category: "charts",
    component: CategoryBars,
    settingsComponent: CategoryBarsSettings,
    buildDefaultRequestBody: BuildDefaultCategoryBarsRequests,
    image: "/img/widgets/category-bars.png",
    tags: [ChartsTag, QueryTag],
  }, {
    id: "category-arcs",
    category: "charts",
    component: CategoryArcs,
    settingsComponent: CategoryArcsSettings,
    buildDefaultRequestBody: BuildDefaultCategoryArcsRequests,
    image: "/img/widgets/category-arcs.png",
    tags: [ChartsTag, QueryTag]
  }, {
    id: "chord-chart",
    category: "charts",
    component: ChordChart,
    settingsComponent: ChordChartSettings,
    buildDefaultRequestBody: BuildDefaultChordChartRequests,
    image: "/img/widgets/chord-chart-source.png",
    tags: [ChartsTag, QueryTag]
  }, {
    id: "radar-chart",
    category: "charts",
    component: RadarChart,
    settingsComponent: RadarChartSettings,
    buildDefaultRequestBody: BuildDefaultRadarChartRequests,
    image: "/img/widgets/radar-chart.png",
    tags: [ChartsTag, QueryTag]
  }, {
    id: "radial-sentiment",
    category: "charts",
    component: RadialSentiment,
    settingsComponent: RadialSentimentSettings,
    buildDefaultRequestBody: BuildDefaultRadialSentimentRequests,
    image: "/img/widgets/radial-sentiment.png",
    tags: [ChartsTag, QueryTag]
  }, {
    id: "pie-chart",
    category: "charts",
    component: PieChart,
    settingsComponent: PieChartSettings,
    buildDefaultRequestBody: BuildDefaultPieChartRequests,
    image: "/img/widgets/pie-chart.png",
    tags: [ChartsTag, QueryTag],
  },
  {
    id: "percent-change",
    category: "charts",
    component: PercentChange,
    settingsComponent: PercentChangeSettings,
    buildDefaultRequestBody: BuildDefaultPercentChangeRequests,
    image: "/img/widgets/percent-change.png",
    tags: [NumberTag, QueryTag]
  },{
    id: "mentions-over-time",
    category: "charts",
    component: MentionsOverTime,
    settingsComponent: MentionsOverTimeSettings,
    buildDefaultRequestBody: BuildDefaultMentionsOverTimeRequests,
    image: "/img/widgets/mentions-over-time.png",
    tags: [NumberTag, QueryTag]
  },{
    id: "ai-social-personas-structured",
    category: "ai",
    component: AISocialPersonasStructured,
    buildDefaultRequestBody: BuildDefaultAISummaryRequests,
    image: "/img/widgets/ai-social-personas.png",
    tags: [AiTag, QueryTag],
    requiredPermissions: ["ai_features", "social_data"],
    contentPath: ".ai-image-content"
  },
  {
    id: "ai-summary-structured",
    category: "ai",
    component: AISummaryStructured,
    settingsComponent: AISummarySettings,
    buildDefaultRequestBody: BuildDefaultAISummaryRequests,
    image: "/img/widgets/ai-summary.png",
    tags: [AiTag, QueryTag],
    requiredPermissions: ["ai_features"],
    contentPath: ".ai-image-content"
  },
  // linguistics widgets
  {
    id: "force-graph",
    category: "linguistics",
    component: ForceGraphWidget,
    settingsComponent: ForceGraphSettings,
    buildDefaultRequestBody: BuildDefaultForceGraphRequests,
    image: "/img/widgets/force-graph.png",
    tags: [ChartsTag, LinguisticsTag, QueryTag]
  }, {
    id: "word-cloud",
    category: "linguistics",
    component: WordCloud,
    settingsComponent: WordCloudSettings,
    buildDefaultRequestBody: BuildDefaultWordCloudRequests,
    image: "/img/widgets/word-cloud.png",
    tags: [ChartsTag, LinguisticsTag, QueryTag],
  }, {
    id: "time-of-day-of-week",
    category: "charts",
    component: TimeOfDayOfWeek,
    settingsComponent: TimeOfDayOfWeekSettings,
    buildDefaultRequestBody: BuildDefaultTimeOfDayOfWeekRequests,
    image: "/img/widgets/time-of-day-of-week.png",
    tags: [ChartsTag, QueryTag]
  }, {
    id: "topics-tree-map",
    category: "linguistics",
    component: TopicsTreeMap,
    settingsComponent: TopicsTreeMapSettings,
    buildDefaultRequestBody: BuildDefaultTopicTreeRequests,
    image: "/img/widgets/topics-tree-map.png",
    tags: [ChartsTag, LinguisticsTag, QueryTag],
  },
  // geography widgets
  {
    id: "choropleth",
    category: "geography",
    component: ChoroplethMapWidget,
    buildDefaultRequestBody: BuildDefaultChoroplethRequests,
    image: "/img/widgets/choropleth.png",
    tags: [GeographyTag, QueryTag]
  }, {
    id: "heatmap",
    category: "geography",
    component: HeatmapMapWidget,
    buildDefaultRequestBody: BuildDefaultHeatmapRequests,
    settingsComponent: HeatmapSettings,
    image: "/img/widgets/heatmap.png",
    tags: [GeographyTag, QueryTag],
  },
  // lists and table widgets
  {
    id: "data-table",
    category: "tables",
    component: DataTable,
    settingsComponent: DataTableSettings,
    buildDefaultRequestBody: BuildDefaultDataTableRequests,
    image: "/img/widgets/data-table.png",
    tags: [TablesTag, QueryTag]
  },
  {
    id: "query-comparison-table",
    category: "tables",
    component: QueryComparisonTable,
    settingsComponent: QueryComparisonTableSettings,
    buildDefaultRequestBody: buildDefaultQueriesRequest,
    image: "/img/widgets/data-table.png",
    tags: [TablesTag, QueryTag]
  },
  {
    id: "embed-list",
    component: EmbedList,
    settingsComponent: EmbedSettings,
    buildDefaultRequestBody: BuildDefaultEmbedListRequests,
    category: "tables",
    image: "/img/widgets/post-embed.png",
    tags: [TablesTag, QueryTag]
  },
  {
    id: "narrative-table",
    category: "tables",
    buildDefaultRequestBody: BuildDefaultNarrativeTableRequests,
    component: NarrativeTableWidget,
    settingsComponent: NarrativeTableSettings,
    image: "/img/widgets/narratives-table.png",
    tags: [TablesTag, QueryTag]
  },
  {
    id: "post-list",
    component: PostList,
    settingsComponent: PostListSettings,
    buildDefaultRequestBody: BuildDefaultPostListRequests,
    category: "tables",
    image: "/img/widgets/post-list.png",
    tags: [TablesTag, QueryTag],
    contentPath: '.post-list-image-content'
  },
  {
    id: "record-table",
    component: RecordTable,
    settingsComponent: RecordTableSettings,
    buildDefaultRequestBody: BuildDefaultRecordTableRequests,
    category: "tables",
    image: "/img/widgets/record-table.png",
    tags: [TablesTag, QueryTag]
  },
  {
    id: "topic-table",
    category: "tables",
    buildDefaultRequestBody: BuildDefaultTopicTableRequests,
    component: TopicTableWidget,
    settingsComponent: TopicTableSettings,
    image: "/img/widgets/topics-table.png",
    tags: [LinguisticsTag, TablesTag, QueryTag]
  },
  {
    id: "social-influencers",
    category: "tables",
    buildDefaultRequestBody: BuildDefaultSocialInfluencersListRequests,
    component: SocialInfluencersList,
    settingsComponent: SocialInfluencersSettings,
    image: "/img/widgets/social-influencers-list.png",
    tags: [TablesTag, QueryTag],
    requiredPermissions: ["social_data"],
    contentPath: '.influencers-image-content'
  },
  {
    id: "share-of-voice",
    category: "charts",
    component: ShareOfVoice,
    settingsComponent: ShareOfVoiceSettings,
    buildDefaultRequestBody: BuildDefaultShareOfVoiceRequests,
    image: "/img/widgets/share-of-voice.png",
    tags: [ChartsTag, QueryTag]
  },
  {
    id: "subqueries",
    category: "charts",
    component: Subqueries,
    settingsComponent: SubqueriesSettings,
    buildDefaultRequestBody: BuildDefaultSubqueriesRequests,
    image: "/img/widgets/subqueries.png",
    tags: [ChartsTag, QueryTag, TablesTag]
  },
  {
    id: "bubble-comparison",
    category: "charts",
    component: BubbleComparison,
    settingsComponent: BubbleComparisonSettings,
    buildDefaultRequestBody: BuildDefaultBubbleComparisonRequests,
    image: "/img/widgets/bubble-comparison.png",
    tags: [ChartsTag, QueryTag]
  },
  // Design widgets
  {
    id: "headline",
    category: "design",
    component: Headline,
    image: "/img/widgets/headline.png",
    tags: [DesignTag, TextTag]
  }, {
    id: "rich-text-editor",
    component: RichTextEditor,
    category: "design",
    image: "/img/widgets/rich-text-editor.png",
    tags: [DesignTag, TextTag]
  }, {
    id: "image",
    component: Image,
    settingsComponent: ImageSettings,
    category: "design",
    image: "/img/widgets/infegy_platform.webp",
    tags: [DesignTag]
  }, {
    id: "color-box",
    component: ColorBox,
    category: "design",
    image: "/img/widgets/color-box.png",
    tags: [DesignTag]
  },
  // Admin widgets
  {
    id: "dashboard-list",
    category: "admin",
    component: DashboardList,
    image: "/img/widgets/dashboard-list.png",
    tags: [AdminTag]
  }, {
    id: "template-search",
    component: TemplateSearch,
    category: "admin",
    image: "/img/widgets/template-search.png",
    tags: [AdminTag]
  }
  // , {
  //   id: "query-test-widget",
  //   component: QueryTestWidget,
  //   settingsComponent: QueryTestWidgetSettingsDialog,
  //   category: "admin",
  //   image: "/img/widgets/query-test-widget.png",
  //   tags: [AdminTag]
  // }
];

const categories: string[] = [];
const widgetsByCategory = widgetRegistry.reduce((memo: {[key: string]: any}, widgetTypeInfo: WidgetRegistryItem) => {
  if (!widgetTypeInfo.category) {
    if (!memo["unknown"]) {
      memo["unknown"] = [];
      categories.push("unknown");
    }
    memo["unknown"].push(widgetTypeInfo);
    return memo;
  }
  if (!memo[widgetTypeInfo.category]) {
    categories.push(widgetTypeInfo.category);
    memo[widgetTypeInfo.category] = [];
  }
  memo[widgetTypeInfo.category].push(widgetTypeInfo);
  return memo;
}, {} as {[key: string]: any});

export function getWidgetTypeInfo(widgetTypeID: string): WidgetRegistryItem {
  let foundWidgetTypeInfo:any = widgetRegistry.find((widget) => widget.id === widgetTypeID);
  if (!foundWidgetTypeInfo) {
    foundWidgetTypeInfo = {
      id: "default",
      component: DefaultWidget,
    };
  }
  return foundWidgetTypeInfo;
}

export function getWidgetCategories() {
  return categories;
}

export function getWidgetTypesByCategory(category: string) {
  return widgetsByCategory[category];
}